import React from 'react';

import noop from 'lodash/noop';
import Button from '@atlaskit/button/standard-button';
import ShareIcon from '@atlaskit/icon/core/share';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { messages } from './messages.tsx';
import { AnalyticsWrapper } from './common/ui/analytics-wrapper/AnalyticsWrapper.tsx';
import type { Props } from './common/types.tsx';

type LoaderProps = {
	triggerButtonStyle?: 'icon-only' | 'icon-with-text';
};

export const ShareLoadingPlaceholder = ({
	triggerButtonStyle = 'icon-only',
}: LoaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div>
			<Button
				appearance="subtle"
				iconBefore={
					<ShareIcon
						spacing="spacious"
						label={formatMessage(messages.shareTriggerButtonText)}
						color={token('color.icon')}
					/>
				}
				isDisabled
			>
				{triggerButtonStyle === 'icon-with-text'
					? formatMessage(messages.shareTriggerButtonText)
					: null}
			</Button>
		</div>
	);
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ShareButtonBase = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-share-button" */ './ui/index.tsx'),
	{ ssr: false },
);

/**
 * @deprecated - Use the Entrypoints-based `ShareButtonAsync` from index.tsx instead.
 */
export const ShareButton = (props: Props): React.JSX.Element => (
	<AnalyticsWrapper>
		<UFOSegment name="async.share.button">
			<JSErrorBoundary id="async.share.button" packageName="share-button" fallback="unmount">
				<Placeholder
					name="share-button"
					fallback={
						props.renderCustomTriggerButton === undefined ? (
							<ShareLoadingPlaceholder triggerButtonStyle={props.triggerButtonStyle} />
						) : (
							<props.renderCustomTriggerButton isDisabled onClick={noop} />
						)
					}
				>
					<ShareButtonBase {...props} />
				</Placeholder>
			</JSErrorBoundary>
		</UFOSegment>
	</AnalyticsWrapper>
);

export default ShareButton;
